import * as React from 'react';
import {
  MasonryEngineNode,
  MasonryEngineNodeAction,
  MasonryEngineNodeRendererProps,
  useMasonryEngineActionEmitter,
} from '@leagueplatform/masonry-engine';
import { NODE_TYPE_CHAT_HISTORY_MESSAGE } from '../../../constants/masonry.constants';
import { ChatHistoryMessage } from '../../../components/chat-history-message/chat-history-message.component';
import type { ChatHistoryMessageProps } from '../../../components/chat-history-message/chat-history-message.types';

type ChatHistoryMessageNodeProperties = Omit<
  ChatHistoryMessageProps,
  'children' | 'onNextClick' | 'onPreviousClick'
>;

type ChatHistoryMessageNodeActions = {
  onNextClick?: MasonryEngineNodeAction;
  onPreviousClick?: MasonryEngineNodeAction;
};

type ChatHistoryMessageNodeSections = {
  items?: MasonryEngineNode<string, any>[];
};

type ChatHistoryMessageNode = MasonryEngineNode<
  typeof NODE_TYPE_CHAT_HISTORY_MESSAGE,
  ChatHistoryMessageNodeProperties,
  ChatHistoryMessageNodeActions,
  ChatHistoryMessageNodeSections
>;

export type ChatHistoryMessageRendererProps =
  MasonryEngineNodeRendererProps<ChatHistoryMessageNode>;

export const ChatHistoryMessageRenderer = ({
  eventType,
  senderName,
  senderAvatar,
  timestamp,
  content,
  contentType,
  items,
  eventText,
  onNextClick,
  onPreviousClick,
}: ChatHistoryMessageRendererProps) => {
  const invokeAction = useMasonryEngineActionEmitter();

  const handleNextClick = React.useCallback(() => {
    if (onNextClick) {
      invokeAction({ ...onNextClick });
    }
  }, [invokeAction, onNextClick]);

  const handlePreviousClick = React.useCallback(() => {
    if (onPreviousClick) {
      invokeAction({ ...onPreviousClick });
    }
  }, [invokeAction, onPreviousClick]);

  return (
    <ChatHistoryMessage
      eventType={eventType}
      senderName={senderName}
      senderAvatar={senderAvatar}
      timestamp={timestamp}
      content={content}
      contentType={contentType}
      eventText={eventText}
      onNextClick={onNextClick ? handleNextClick : undefined}
      onPreviousClick={onPreviousClick ? handlePreviousClick : undefined}
    >
      {items}
    </ChatHistoryMessage>
  );
};
